import type { AiInsightResponse, InsightBrief, Meal, Symptom, VerifiedFinding, WeeklyInsight } from './types';
import { verifyAll } from './insightVerify';
import { assembleInsight } from './insightAssemble';
import { guardHeadlineSummary } from './insightGuard';
import { clearlyAbove, SOLID_MIN_MEALS } from './insightOverlaps';
import { normaliseIngredientName } from './ingredientTaxonomy';

/** Normalised, order-free key for a combination or pattern: items (sorted) plus its condition. */
export function candidateKey(items: string[], condition?: string | null): string {
  const names = items.map((i) => normaliseIngredientName(i)).sort().join('+');
  return `${names}|${(condition ?? '').trim().toLowerCase()}`;
}

/**
 * The verified findings the phone shows. Ingredient and group findings pass through. A combination or
 * pattern is shown only when it is one of the engine's candidates (same items and condition) and the
 * phone's own recount is still clearly above the usual flare rate.
 */
export function filterShownFindings(findings: VerifiedFinding[], brief?: InsightBrief | null): VerifiedFinding[] {
  const candidates = new Set((brief?.patternCandidates ?? []).map((c) => candidateKey(c.items, c.condition)));
  return findings.filter((f) => {
    if (f.kind !== 'combination' && f.kind !== 'pattern') return true;
    if (!candidates.has(candidateKey(f.items, f.condition))) return false;
    const baseline = typeof f.verifiedBaseline === 'number' ? f.verifiedBaseline : brief?.baselineFlareRate;
    if (typeof baseline !== 'number') return false;
    return clearlyAbove(f.verifiedFlares, f.verifiedCount, baseline);
  });
}

/** "Limited evidence" follows the phone's own recount: fewer than SOLID_MIN_MEALS matching meals. */
export function isLimitedEvidence(f: VerifiedFinding): boolean {
  return f.verifiedCount < SOLID_MIN_MEALS;
}

export interface InsightView {
  /** The AI response with headline and summary re-guarded against the findings actually shown. */
  ai: AiInsightResponse | null;
  /** Verified findings that pass the phone's candidate check. */
  findings: VerifiedFinding[];
}

/**
 * Read-time view of a v2 insight. Verified findings are derived from the diary when the insight does
 * not carry them (the cloud copy never does), filtered to engine candidates, and the headline guard is
 * re-run so the headline and summary only name what is shown or a kept engine lead. Never rewrites the
 * stored insight.
 */
export function deriveInsightView(insight: WeeklyInsight, meals: Meal[], symptoms: Symptom[]): InsightView {
  const ai = insight.ai ?? null;
  if (!ai) return { ai: null, findings: [] };
  let verified: VerifiedFinding[] = [];
  try {
    verified = insight.verified_findings ?? verifyAll(ai, meals, symptoms);
  } catch {
    verified = [];
  }
  const findings = filterShownFindings(verified, insight.brief);
  if (!insight.brief) return { ai, findings };
  try {
    const guarded = guardHeadlineSummary({ ...ai, findings }, insight.brief).ai;
    return { ai: { ...ai, headline: guarded.headline, summary: guarded.summary }, findings };
  } catch {
    return { ai, findings };
  }
}

/** Turn a server response into a v2 insight, falling back to a local brief-only insight. */
export function resolveInsightResponse(
  status: number,
  body: unknown,
  brief: InsightBrief,
  meals: Meal[],
  symptoms: Symptom[],
): WeeklyInsight {
  const ok = status >= 200 && status < 300;
  if (ok && body && typeof body === 'object' && !Array.isArray(body) && (body as any).format_version === 2) {
    const insight = body as WeeklyInsight;
    const ai = insight.ai ?? null;
    const findings = ai
      ? filterShownFindings(verifyAll(ai, meals, symptoms), insight.brief ?? brief)
      : [];
    return { ...insight, verified_findings: findings };
  }
  return assembleInsight(brief, null, 'brief-only', new Date().toISOString());
}
